// src/pages/projects/ProjectListTable.tsx
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import type { Project } from './types';
import { formatMoney } from '../../utils/formatter';
import { useQueryClient } from '@tanstack/react-query';

/** ProgressStatus 라벨/배지 맵 */
type PS = NonNullable<Project['progressStatusCached']>;
const STATUS_LABEL: Record<PS, string> = {
    NOT_STARTED: '미착수',
    ON_TRACK: '정상',
    WATCH: '주의',
    DELAYED: '지연',
    RISK: '위험',
    COMPLETED: '완료',
};

const STATUS_BADGE: Record<PS, string> = {
    NOT_STARTED: 'bg-gray-100 text-gray-600',
    ON_TRACK: 'bg-blue-100 text-blue-700',
    WATCH: 'bg-amber-100 text-amber-700',
    DELAYED: 'bg-red-100 text-red-700',
    RISK: 'bg-rose-100 text-rose-700',
    COMPLETED: 'bg-green-100 text-green-700',
};

type Props = {
    items: Project[];
    loading: boolean;
    err: string | null;
    onEdit: (p: Project) => void;
};

/** 정렬 키(모두 Project의 옵셔널 필드로 가정) */
type SortKey =
    | 'code'
    | 'name'
    | 'contractDate'
    | 'contractPrice'
    | 'plannedProgressCached'
    | 'actualProgressCached'
    | 'progressStatusCached'
    | 'priority'
    | 'endDate'
    | 'updatedAt';

export default function ProjectListTable({ items, loading, err, onEdit }: Props) {
    const [sortKey, setSortKey] = useState<SortKey>('updatedAt');
    const [sortAsc, setSortAsc] = useState<boolean>(false);
    const [statusFilter, setStatusFilter] = useState<'ALL' | PS>('ALL');
    const nav = useNavigate();
    const qc = useQueryClient();

    /** 안전한 값 비교 유틸 (number/string/date) */
    const cmp = (a: unknown, b: unknown): number => {
        if (a == null && b == null) return 0;
        if (a == null) return 1;
        if (b == null) return -1;

        const maybeDateA = typeof a === 'string' && /^\d{4}-\d{2}-\d{2}/.test(a);
        const maybeDateB = typeof b === 'string' && /^\d{4}-\d{2}-\d{2}/.test(b);

        if (maybeDateA || maybeDateB) {
            const ta = new Date(String(a)).getTime();
            const tb = new Date(String(b)).getTime();
            return ta - tb;
        }

        if (typeof a === 'number' && typeof b === 'number') return a - b;
        return String(a).localeCompare(String(b));
    };

    const sorted = useMemo(() => {
        let list = [...items];

        if (statusFilter !== 'ALL') {
            list = list.filter((p) => p.progressStatusCached === statusFilter);
        }

        list.sort((x, y) => {
            const va = (x as any)[sortKey];
            const vb = (y as any)[sortKey];
            const base = cmp(va, vb);
            return sortAsc ? base : -base;
        });

        return list;
    }, [items, statusFilter, sortKey, sortAsc]);

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) setSortAsc((v) => !v);
        else {
            setSortKey(key);
            setSortAsc(true);
        }
    };

    const StatusBadge = ({ status }: { status?: Project['progressStatusCached'] }) => {
        if (!status) return <span className="text-gray-400">—</span>;

        return (
            <span
                className={clsx(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                    STATUS_BADGE[status],
                )}
            >
                {STATUS_LABEL[status]}
            </span>
        );
    };

    const d = (v?: string | null) => (v ? new Date(v).toLocaleDateString() : '—');

    if (loading) return <div className="p-4">로딩중…</div>;
    if (err) return <div className="p-4 text-sm text-red-600">{err}</div>;
    if (items.length === 0) return <div className="p-4 text-sm text-gray-600">프로젝트가 없습니다.</div>;

    const onOpenProject = (project: any) => {
        qc.setQueryData(['project', project.id], {
            ok: true,
            data: {
                id: project.id,
                name: project.name,
                code: project.code,
                status: project.status,
            },
        });

        nav(`/projects/${project.id}`, {
            state: { projectName: project.name },
        });
    };

    const columns: Array<[string, string]> = [
        ['code', '코드'],
        ['name', '이름'],
        ['contractDate', '계약일'],
        ['contractCurrency', '통화'],
        ['contractPrice', '계약가'],
        ['incoterms', 'Incoterms'],
        ['projectType', '유형'],
        ['client', '클라이언트'],
        ['destinationCountry', '도착국가'],
        ['startDate', '시작일'],
        ['endDate', '종료일'],
        ['plannedProgressCached', '계획진도'],
        ['actualProgressCached', '실제진도'],
        ['progressStatusCached', '상태'],
        ['priority', '우선순위'],
        ['updatedAt', '최근수정'],
    ];

    const sortable: SortKey[] = [
        'code',
        'name',
        'contractDate',
        'contractPrice',
        'plannedProgressCached',
        'actualProgressCached',
        'progressStatusCached',
        'priority',
        'endDate',
        'updatedAt',
    ];

    return (
        <div className="relative">
            <div className="flex flex-wrap gap-2 border-b bg-gray-50 p-3 text-xs">
                {(['ALL', 'NOT_STARTED', 'ON_TRACK', 'WATCH', 'DELAYED', 'RISK', 'COMPLETED'] as const).map((s) => (
                    <button
                        key={s}
                        className={clsx(
                            'rounded-full border px-3 py-1',
                            statusFilter === s ? 'border-sky-400 bg-sky-100 text-sky-700' : 'hover:bg-gray-100',
                        )}
                        onClick={() => setStatusFilter(s as any)}
                        type="button"
                    >
                        {s === 'ALL' ? '전체' : STATUS_LABEL[s as PS]}
                    </button>
                ))}
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-max text-xs">
                    <thead className="bg-gray-50 text-gray-600">
                        <tr>
                            {columns.map(([key, label]) => {
                                const isSortable = sortable.includes(key as SortKey);

                                return (
                                    <th
                                        key={key}
                                        className={clsx(
                                            'whitespace-nowrap px-4 py-2 text-left font-medium',
                                            isSortable && 'cursor-pointer select-none',
                                        )}
                                        onClick={isSortable ? () => toggleSort(key as SortKey) : undefined}
                                        title={isSortable ? '클릭하여 정렬' : undefined}
                                    >
                                        {label}
                                        {isSortable && sortKey === key && <span>{sortAsc ? ' ▲' : ' ▼'}</span>}
                                    </th>
                                );
                            })}
                            <th className="whitespace-nowrap px-4 py-2">Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {sorted.map((p) => {
                            const clientText =
                                (p as any).clientName ?? (p as any).client?.name ?? (p as any).client ?? '—';

                            return (
                                <tr key={p.id} className="border-t hover:bg-blue-50 text-xs">
                                    <td className="whitespace-nowrap px-4 py-2">{p.code ?? '—'}</td>

                                    <td className="max-w-[280px] px-4 py-2">
                                        <span
                                            className="block cursor-pointer truncate text-sky-700 hover:text-sky-900"
                                            onClick={() => onEdit(p)}
                                            title={p.name ?? '—'}
                                        >
                                            {p.name ?? '—'}
                                        </span>
                                    </td>

                                    <td className="whitespace-nowrap px-4 py-2">{d(p.contractDate as any)}</td>
                                    <td className="whitespace-nowrap px-4 py-2">{p.contractCurrency ?? '—'}</td>
                                    <td className="whitespace-nowrap px-4 py-2">
                                        {formatMoney(p.contractPrice ?? null, p.contractCurrency ?? undefined)}
                                    </td>
                                    <td className="whitespace-nowrap px-4 py-2">{(p as any).incoterms ?? '—'}</td>
                                    <td className="whitespace-nowrap px-4 py-2">{(p as any).projectType ?? '—'}</td>

                                    <td className="max-w-[220px] px-4 py-2">
                                        <span className="block truncate" title={clientText}>
                                            {clientText}
                                        </span>
                                    </td>

                                    <td className="whitespace-nowrap px-4 py-2">
                                        {(p as any).destinationCountry ?? '—'}
                                    </td>
                                    <td className="whitespace-nowrap px-4 py-2">{d(p.startDate as any)}</td>
                                    <td className="whitespace-nowrap px-4 py-2">{d(p.endDate as any)}</td>

                                    <td className="whitespace-nowrap px-4 py-2">
                                        {typeof p.plannedProgressCached === 'number'
                                            ? `${p.plannedProgressCached.toFixed(1)}%`
                                            : '—'}
                                    </td>

                                    <td className="whitespace-nowrap px-4 py-2">
                                        {typeof p.actualProgressCached === 'number'
                                            ? `${p.actualProgressCached.toFixed(1)}%`
                                            : '—'}
                                    </td>

                                    <td className="whitespace-nowrap px-4 py-2">
                                        {p.progressStatusCached ? (
                                            <StatusBadge status={p.progressStatusCached} />
                                        ) : (
                                            <span className="text-gray-400">—</span>
                                        )}
                                    </td>

                                    <td className="whitespace-nowrap px-4 py-2">{(p as any).priority ?? '—'}</td>
                                    <td className="whitespace-nowrap px-4 py-2">{d(p.updatedAt as any)}</td>

                                    <td className="whitespace-nowrap px-4 py-2 text-right">
                                        {/* <button
                                            className="mr-1 inline-flex items-center rounded-lg border px-3 py-1.5 text-xs hover:bg-gray-100"
                                            onClick={() => onEdit(p)}
                                            type="button"
                                        >
                                            프로젝트 수정
                                        </button> */}
                                        <button
                                            className="mr-1 inline-flex items-center rounded-lg border px-3 py-1.5 text-xs hover:bg-gray-100"
                                            onClick={() => onOpenProject(p)}
                                            type="button"
                                        >
                                            프로젝트 열기
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}